import React from "react";
import { useLocation } from "react-router-dom";
import { LayoutDashboard, Search } from "lucide-react";
import { SECTIONS, findSection, findActiveTab } from "./navConfig";
import JobScopeSelector from "./JobScopeSelector";
import ProfileDropdown from "./ProfileDropdown";
import NotificationBell from "./NotificationBell";

// Fallback titles for routes that don't belong to a sidebar section
// (deep links, builders, account pages). Section routes resolve via navConfig.
const FALLBACK_TITLES = {
  "/admin/dashboard": { title: "Dashboard", crumb: "Dashboard" },
  "/admin/create-job": { title: "Create job", crumb: "Jobs / Create" },
  "/admin/generate-jd": { title: "Generate JD", crumb: "Jobs / Generate" },
  "/admin/interview-report": { title: "Interview report", crumb: "Interviews / Report" },
  "/admin/rubrics": { title: "Rubrics", crumb: "Interviews / Rubrics" },
  "/admin/offers": { title: "Offers", crumb: "Pipeline / Offers" },
  "/admin/assessments": { title: "Assessments", crumb: "Jobs / Assessments" },
  "/admin/analytics": { title: "Analytics", crumb: "Reports / Analytics" },
  "/admin/my-account": { title: "My account", crumb: "Settings / Account" },
  "/admin/preferences": { title: "Preferences", crumb: "Settings / Preferences" },
  "/admin/help-support": { title: "Help & support", crumb: "Settings / Help" },
};

export default function TopBar() {
  const loc = useLocation();
  const section = findSection(loc.pathname);
  const tab = findActiveTab(section, loc.pathname);
  const fallback = FALLBACK_TITLES[loc.pathname];

  const Icon = section?.Icon || LayoutDashboard;
  const color = section?.color || "#7B5AC4";
  const title = section?.label || fallback?.title || "AmpleHire";
  const crumb = section ? tab?.label || section.desc || "" : fallback?.crumb || "";

  return (
    <div
      style={{
        position: "relative",
        height: 60,
        background: "#FFFFFF",
        borderBottom: "1px solid rgba(0,0,0,0.08)",
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "0 24px",
        flexShrink: 0,
      }}
    >
      {/* Left: module icon + title/crumb */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0, flexShrink: 1 }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: `${color}1F`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Icon size={18} style={{ color }} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div className="text-truncate" style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 18, fontWeight: 700, color: "#1A2332", lineHeight: 1.2 }}>
            {title}
          </div>
          <div className="hidden md:block" style={{ fontSize: 11, color: "#64748B", lineHeight: 1.2 }}>
            {crumb}
          </div>
        </div>
      </div>

      {/* Right: scope + search + notifications + profile */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginLeft: "auto" }}>
        <JobScopeSelector />
        <button
          type="button"
          onClick={() => window.dispatchEvent(new CustomEvent("amplehire:open-command-palette"))}
          aria-label="Open command palette (Ctrl+K)"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            height: 32,
            padding: "0 10px",
            borderRadius: 8,
            background: "var(--ah-surface)",
            border: "1px solid var(--ah-border)",
            color: "#64748B",
            fontSize: 13,
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          <Search size={14} />
          <span className="hidden md:inline">Search</span>
          <kbd className="font-mono hidden sm:inline" style={{ fontSize: 11, color: "#64748B", background: "#FFFFFF", border: "1px solid var(--ah-border)", borderRadius: 4, padding: "1px 5px" }}>
            ⌘K
          </kbd>
        </button>
        <NotificationBell />
        <ProfileDropdown />
      </div>

      {/* Bottom gradient accent line */}
      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 2, background: "linear-gradient(to right, #7B5AC4, #1F8A7D, transparent 50%)" }} />
    </div>
  );
}
